// @ts-nocheck
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { trpc, trpcClient } from '@/lib/trpc';
import { toast } from 'sonner';
import {
  Printer,
  CheckCircle2,
  Clock,
  Layers,
  Download,
  FileSpreadsheet,
  Send,
  Flame,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn, formatNumber } from '@/lib/utils';
import { ListPageShell } from '@/components/shared/ListPageShell';
import { CompactKpiRibbon, type CompactKpiItem } from '@/components/shared/CompactKpiRibbon';
import { useHeaderSearch } from '@/hooks/useHeaderSearch';
import { useRowSelection } from '@/hooks/useRowSelection';
import { DataTableRow, DataTableRowCheckbox, DataTableSelectAllCheckbox } from '@/components/shared/DataTableRow';
import { BulkActionBar } from '@/components/shared/BulkActionBar';
import { exportToExcel } from '@/lib/excel.utils';
import { emsTabClass } from "@/components/ui/ems-tabs";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

const plateLabel = (po: any) =>
  po.isPlateReady ? 'Kẽm sẵn sàng' : po.isPlateBurning ? 'Đang ghi bản CTP' : 'Chờ ghi bản';

const EXPORT_COLUMNS = [
  { key: 'orderId', label: 'Mã LSX', width: 16 },
  { key: 'productName', label: 'Sản phẩm', width: 36 },
  { key: 'customer', label: 'Khách hàng', width: 20, exportFn: (po: any) => po.customer?.shortName || po.customer?.name || '' },
  { key: 'sheetSize', label: 'Khổ giấy xén', width: 14 },
  { key: 'ups', label: 'Bát', width: 8, exportFn: (po: any) => po.ups || 1 },
  { key: 'netSheets', label: 'Tờ mộc (Net)', width: 12, exportFn: (po: any) => po.netSheets || 0 },
  { key: 'runSheetsNeeded', label: 'Tờ chạy (OK)', width: 12, exportFn: (po: any) => po.runSheetsNeeded || 0 },
  { key: 'plateStatus', label: 'Trạng thái kẽm', width: 16, exportFn: plateLabel },
  { key: 'moldStatus', label: 'Xén mộc', width: 12, exportFn: (po: any) => (po.moldStatus === 'READY' ? 'Đã xén' : 'Chờ xén') },
  { key: 'prepressTotal', label: 'Tổng (phút)', width: 10, exportFn: (po: any) => (po.prepressMinutes as any)?.total || 0 },
];

export function PrepressQueuePage() {
  const navigate = useNavigate();
  const { headerSearch } = useHeaderSearch();
  const [filterCtp, setFilterCtp] = useState<string>('ALL');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(25);

  useEffect(() => {
    setPage(1);
  }, [headerSearch, filterCtp]);

  // Fetch Prepress Capacity Queue Items
  const { data, isLoading, refetch } = useQuery(
    trpc.productionOrders.getPrepressCapacityQueue.queryOptions({
      search: headerSearch || undefined,
    })
  );

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    if (filterCtp === 'ALL') return data.items;
    if (filterCtp === 'READY') {
      return data.items.filter((item: any) => item.isPlateReady);
    }
    if (filterCtp === 'BURNING') {
      return data.items.filter((item: any) => item.isPlateBurning);
    }
    return data.items.filter((item: any) => !item.isPlateReady);
  }, [data?.items, filterCtp]);

  const pagedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  const rowIds = useMemo(() => pagedItems.map((po: any) => po.id), [pagedItems]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedIds, selectedCount, clearSelection } = rowSelection;
  const selectedRows = pagedItems.filter((po: any) => selectedIds.has(po.id));
  const openDetail = (po: any) => navigate({ to: '/production-orders/$orderId', params: { orderId: po.orderId || po.id } });

  const runBulk = async (rows: any[], fn: (po: any) => Promise<any>, okMsg: string) => {
    let ok = 0;
    for (const po of rows) {
      try {
        await fn(po);
        ok++;
      } catch (err: any) {
        toast.error(`${po.orderId}: ${err.message || 'Lỗi'}`);
      }
    }
    if (ok) toast.success(`${okMsg}: ${ok} lệnh`);
    clearSelection();
    refetch();
  };

  const readyPlateCount = data?.items?.filter((x: any) => x.isPlateReady).length || 0;
  const burningPlateCount = data?.items?.filter((x: any) => x.isPlateBurning).length || 0;
  const pendingPlateCount = (data?.totalOrders || 0) - readyPlateCount;

  const kpiItems = useMemo<CompactKpiItem[]>(() => [
    {
      key: 'ALL',
      label: 'Tổng bài in',
      count: formatNumber(data?.totalOrders || 0),
      icon: Printer,
      variant: 'purple',
      tooltip: 'Tổng số bài in chờ chế bản CTP',
      clickable: true,
      onClick: () => { setFilterCtp('ALL'); setPage(1); },
    },
    {
      key: 'SHEETS',
      label: 'Tờ in cần chạy (OK)',
      count: formatNumber(data?.totalSheets || 0),
      icon: Layers,
      variant: 'blue',
      tooltip: 'Bao gồm tờ bù hao chạy máy',
    },
    {
      key: 'HOURS',
      label: 'Tổng TG máy dự kiến',
      count: `${data?.totalHours || 0} Giờ`,
      icon: Clock,
      variant: 'amber',
      tooltip: 'Phụ tải định mức trên toàn bộ xưởng',
    },
    {
      key: 'READY',
      label: 'Kẽm sẵn sàng',
      count: `${readyPlateCount} / ${data?.totalOrders || 0}`,
      icon: CheckCircle2,
      variant: 'emerald',
      tooltip: 'Bấm để lọc bài in đã hoàn tất kẽm',
      clickable: true,
      onClick: () => { setFilterCtp((prev) => (prev === 'READY' ? 'ALL' : 'READY')); setPage(1); },
    },
    {
      key: 'BURNING',
      label: 'Đang ghi bản CTP',
      count: formatNumber(burningPlateCount),
      icon: Flame,
      variant: 'amber',
      tooltip: 'Bấm để lọc bài in đang ghi bản CTP',
      clickable: true,
      onClick: () => { setFilterCtp((prev) => (prev === 'BURNING' ? 'ALL' : 'BURNING')); setPage(1); },
    },
    {
      key: 'PENDING',
      label: 'Chờ ghi bản kẽm',
      count: formatNumber(pendingPlateCount),
      icon: Clock,
      variant: 'slate',
      tooltip: 'Bấm để lọc bài in đang chờ ghi bản kẽm',
      clickable: true,
      onClick: () => { setFilterCtp((prev) => (prev === 'PENDING' ? 'ALL' : 'PENDING')); setPage(1); },
    },
  ], [data?.totalOrders, data?.totalSheets, data?.totalHours, readyPlateCount, burningPlateCount, pendingPlateCount]);

  const handleDispatchCtp = async (poId: string) => {
    try {
      setProcessingId(poId);
      await trpcClient.productionOrders.dispatchCtpPlates.mutate({ id: poId });
      toast.success('Đã chuyển trạng thái lệnh sang ĐANG GHI BẢN KẼM!');
      refetch();
    } catch (err: any) {
      toast.error(err.message || 'Lỗi gửi lệnh ghi bản kẽm');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCompleteCtp = async (poId: string) => {
    try {
      setProcessingId(poId);
      const res: any = await trpcClient.productionOrders.completeCtpPlates.mutate({
        id: poId,
        notes: 'Xác nhận hoàn thành kẽm từ Bảng Hàng Chờ Chế Bản CTP',
      });
      toast.success('Đã xác nhận hoàn thành kẽm, kho kẽm đã trừ số lá vừa xuất', res?.stockWarnings?.length ? { description: res.stockWarnings.join(' ') } : undefined);
      refetch();
    } catch (err: any) {
      toast.error(err.message || 'Lỗi hoàn thành kẽm');
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <>
    <ListPageShell
      onExport={() => exportToExcel(filteredItems, EXPORT_COLUMNS, 'hang_cho_che_ban_ctp', { sheetName: 'Chờ CTP' })}
      unitLabel="bài in CTP"
      totalItems={filteredItems.length}
      currentPage={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(newSize) => {
        setPageSize(newSize);
        setPage(1);
      }}
      isLoading={isLoading}
      topBanner={
        <CompactKpiRibbon
          items={kpiItems}
          activeKey={filterCtp}
          onSelect={(key) => {
            if (key && ['ALL', 'READY', 'BURNING', 'PENDING'].includes(key)) {
              setFilterCtp((prev) => (prev === key ? 'ALL' : key));
              setPage(1);
            }
          }}
          isLoading={isLoading}
        />
      }
      tabs={
        <div className="flex items-center gap-4">
          <button
            type="button"
            className={emsTabClass(true, { size: "sm" })}
          >
            <Printer className="w-3.5 h-3.5 text-indigo-600" />
            <span>Chờ Ghi Bản Kẽm CTP</span>
            <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-primary/10 text-primary tabular-nums">
              {data?.totalOrders || 0}
            </span>
          </button>
        </div>
      }
      actions={
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            onClick={() => navigate({ to: '/production-orders' })}
            className="gap-1.5 text-xs h-8 bg-purple-600 hover:bg-purple-700 text-white"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            DS Lệnh SX
          </Button>
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse apv-compact-table">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 font-medium">
              <DataTableSelectAllCheckbox rowSelection={rowSelection} />
              <th className="py-2.5 px-3 w-28">Mã LSX</th>
              <th className="py-2.5 px-3 min-w-[200px]">Sản Phẩm &amp; Bài In</th>
              <th className="py-2.5 px-3 w-28">Khách Hàng</th>
              <th className="py-2.5 px-3 w-28 text-center">Khổ Giấy Xén</th>
              <th className="py-2.5 px-3 w-16 text-center">Bát</th>
              <th className="py-2.5 px-3 w-24 text-right">Tờ Mộc (Net)</th>
              <th className="py-2.5 px-3 w-24 text-right bg-blue-50/50 text-blue-700">Tờ Chạy (OK)</th>
              <th className="py-2.5 px-3 text-center w-36">Trạng Thái Kẽm CTP</th>
              <th className="py-2.5 px-3 text-center w-28">Xén Mộc</th>
              <th className="py-2.5 px-3 text-right font-bold text-purple-700 w-24 bg-purple-50/30">Tổng (p)</th>
              <th className="py-2.5 px-3 text-right w-36 pr-4">Thao Tác Chế Bản</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={12} className="py-8 text-center text-gray-400">
                  Đang tải hàng chờ chế bản CTP...
                </td>
              </tr>
            ) : pagedItems.length === 0 ? (
              <tr>
                <td colSpan={12} className="py-8 text-center text-gray-400">
                  Không tìm thấy bài in chờ CTP phù hợp
                </td>
              </tr>
            ) : (
              pagedItems.map((po: any) => {
                const prepress = (po.prepressMinutes as any) || {};
                const isXen = po.moldStatus === 'READY';
                const isReadyCtp = po.isPlateReady;
                const isBurning = po.isPlateBurning;

                return (
                  <DataTableRow
                    key={po.id}
                    id={po.id}
                    rowSelection={rowSelection}
                    onClick={() => openDetail(po)}
                    className="hover:bg-gray-50/80 transition-colors"
                  >
                    <DataTableRowCheckbox id={po.id} rowSelection={rowSelection} />
                    <td className="py-2.5 px-3 font-mono font-bold text-indigo-700">
                      {po.orderId}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-semibold text-gray-900 line-clamp-1">{po.productName}</div>
                      <div className="text-[11px] text-gray-400 font-mono">Đặt: {formatNumber(po.qtyOrder || 0)} cái</div>
                    </td>
                    <td className="py-2.5 px-3 text-gray-700 font-medium">
                      {po.customer?.shortName || po.customer?.name || '---'}
                    </td>
                    <td className="py-2.5 px-3 text-center font-mono text-gray-600 bg-gray-50/50">
                      {po.sheetSize || '---'}
                    </td>
                    <td className="py-2.5 px-3 text-center font-mono font-bold text-indigo-600">
                      {po.ups || 1}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-600">
                      {formatNumber(po.netSheets || 0)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-bold text-blue-700 bg-blue-50/20">
                      {formatNumber(po.runSheetsNeeded || 0)}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      {isReadyCtp ? (
                        <Badge className="bg-emerald-600 text-white font-medium text-[11px] py-0.5">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Kẽm Sẵn Sàng
                        </Badge>
                      ) : isBurning ? (
                        <Badge variant="outline" className="bg-amber-100 text-amber-900 border-amber-300 font-medium text-[11px] py-0.5 animate-pulse">
                          <Flame className="h-3 w-3 mr-1 text-amber-600" /> Đang Ghi Bản CTP
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 font-medium text-[11px] py-0.5">
                          <Clock className="h-3 w-3 mr-1 text-slate-500" /> Chờ Ghi Bản ({po.plateCount || 4} kẽm)
                        </Badge>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <Badge
                        variant="outline"
                        className={
                          isXen
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700 font-medium text-[11px]'
                            : 'bg-amber-50 border-amber-200 text-amber-700 font-medium text-[11px]'
                        }
                      >
                        {isXen ? 'ĐÃ XÉN GIẤY' : 'CHỜ XÉN'}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-bold text-purple-700 bg-purple-50/30">
                      {prepress.total ? `${prepress.total} p` : '-'}
                    </td>
                    <td className="py-2.5 px-3 text-right pr-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {!isReadyCtp && !isBurning && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[11px] border-blue-200 text-blue-700 hover:bg-blue-50"
                            onClick={() => handleDispatchCtp(po.id)}
                            disabled={processingId === po.id}
                            title="Gửi lệnh ghi bản kẽm sang máy ghi CTP"
                          >
                            <Send className="h-3 w-3 mr-1 text-blue-600" /> Ghi bản
                          </Button>
                        )}
                        {isBurning && (
                          <Button
                            size="sm"
                            className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => handleCompleteCtp(po.id)}
                            disabled={processingId === po.id}
                            title="Xác nhận ghi bản xong kẽm & trừ lá nhôm"
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Xong
                          </Button>
                        )}
                      </div>
                    </td>
                  </DataTableRow>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </ListPageShell>
    <BulkActionBar
      selectedCount={selectedCount}
      onClearSelection={clearSelection}
      actions={[
        {
          label: 'Xuất Excel',
          icon: <Download className="h-3.5 w-3.5" />,
          onClick: () => exportToExcel(selectedRows, EXPORT_COLUMNS, 'hang_cho_che_ban_ctp', { sheetName: 'Chờ CTP' }),
        },
        {
          label: 'Gửi ghi bản kẽm',
          icon: <Send className="h-3.5 w-3.5" />,
          disabled: !selectedRows.some((po: any) => !po.isPlateReady && !po.isPlateBurning),
          onClick: () =>
            runBulk(
              selectedRows.filter((po: any) => !po.isPlateReady && !po.isPlateBurning),
              (po) => trpcClient.productionOrders.dispatchCtpPlates.mutate({ id: po.id }),
              'Đã gửi ghi bản kẽm',
            ),
        },
        {
          label: 'Xong kẽm',
          icon: <CheckCircle2 className="h-3.5 w-3.5" />,
          disabled: !selectedRows.some((po: any) => po.isPlateBurning),
          onClick: () =>
            runBulk(
              selectedRows.filter((po: any) => po.isPlateBurning),
              (po) => trpcClient.productionOrders.completeCtpPlates.mutate({ id: po.id, notes: 'Xác nhận hoàn thành kẽm từ Bảng Hàng Chờ Chế Bản CTP' }),
              'Đã xác nhận xong kẽm',
            ),
        },
      ]}
    />
    </>
  );
}

export default PrepressQueuePage;
